import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Backdrop,
  Box,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close, SwitchAccessShortcutOutlined } from "@mui/icons-material";
import { twMerge } from "tailwind-merge";

import InformationTile from "components/Shared/InformationTile";
import HoverablePaper from "components/Shared/HoverablePaper";
import ReactSelect from "components/Shared/ReactSelect";

import { setUser } from "store/authSlice";
import { baseApi } from "store/services/baseApi";

import { useLocalStorage } from "hooks/useLocalStorage";
import { useAuth } from "hooks/useAuth";

import { useSidebarLayoutContext } from "contexts/SidebarLayoutContext";

const EntitySwitch = () => {
  const [showEntitySwitch, setShowEntitySwitch] = React.useState(false);

  const { setShowSidebar } = useSidebarLayoutContext();

  const { userEntities } = useAuth();

  // methods
  const handleToggleShow = () => {
    setShowEntitySwitch((prev) => !prev);
    setShowSidebar(showEntitySwitch);
  };

  return (
    <>
      <Tooltip title="Switch Entity/Role" arrow>
        <IconButton color="primary" onClick={handleToggleShow}>
          <SwitchAccessShortcutOutlined />
        </IconButton>
      </Tooltip>

      <Backdrop
        open={showEntitySwitch}
        onClick={handleToggleShow}
        sx={{
          color: "#fff",
          backdropFilter: "blur(10px)",
          zIndex: (theme) => theme.zIndex.drawer + 10,
        }}
      >
        <Container
          maxWidth="md"
          style={{ position: "absolute", top: 50 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Paper variant="outlined">
            <Box
              paddingX={2}
              paddingY={1}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography>Switch Entity/Role</Typography>

              <IconButton onClick={handleToggleShow}>
                <Close />
              </IconButton>
            </Box>

            <Divider />

            <div className="relative max-h-[500px] overflow-y-scroll">
              {showEntitySwitch && (
                <div className="flex flex-col gap-4 p-4">
                  {userEntities
                    ?.filter((ue) => ue?.isActive)
                    ?.map((el, index) => (
                      <React.Fragment key={index}>
                        <EntityItem {...el} handleClose={handleToggleShow} />
                      </React.Fragment>
                    ))}
                </div>
              )}
            </div>
          </Paper>
        </Container>
      </Backdrop>
    </>
  );
};

export default EntitySwitch;

const EntityItem = ({
  entity,
  authorizations,
  isActive,
  defaultAuthorization,
  userGroup,
  userType,
  handleClose,
}) => {
  const [userCredential] = useLocalStorage("userInfo");

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const viewEntity = React.useMemo(
    () => userCredential?.viewEntity,
    [userCredential]
  );

  const isActiveEntity = entity?._id === viewEntity?.entity?._id;

  const mappedAuthorizations = authorizations?.map((el) => ({
    label: el?.role,
    value: el?._id,
  }));

  // methods
  const handleSwitchAuthorization = (authorization) => {
    const selectedAccess = authorizations?.find(
      (el) => el?._id === authorization?.value
    );

    dispatch(
      setUser({
        ...userCredential,
        viewEntity: { ...viewEntity, authorization: selectedAccess },
      })
    );

    dispatch(baseApi.util.resetApiState());

    navigate("/app/crm/dashboard");

    handleClose();
  };

  const handleSwitchEntity = () => {
    const newViewEntity = {
      entity: entity,
      userType: userType,
      userGroup: userGroup,
      authorization: defaultAuthorization,
      isActive: isActive,
    };

    dispatch(
      setUser({
        ...userCredential,
        viewEntity: newViewEntity,
      })
    );

    dispatch(baseApi.util.resetApiState());

    navigate("/app/crm/dashboard");

    handleClose();
  };

  if (!entity) return;

  return isActiveEntity ? (
    <HoverablePaper variant="outlined">
      <div
        className={twMerge(
          "flex flex-shrink-0 items-center justify-between p-4",
          isActiveEntity && "bg-blue-50 dark:bg-slate-800"
        )}
      >
        <div className="font-medium">{entity?.name}</div>

        {true ? (
          <ReactSelect
            options={mappedAuthorizations}
            placeholder="Select Authorization"
            onChange={handleSwitchAuthorization}
            value={{
              label: viewEntity?.authorization?.role,
              value: viewEntity?.authorization?._id,
            }}
          />
        ) : (
          <CircularProgress size={24} />
        )}
      </div>
    </HoverablePaper>
  ) : (
    <HoverablePaper variant="outlined">
      <div
        className="flex cursor-pointer items-center justify-between p-4"
        onClick={handleSwitchEntity}
      >
        <div className="font-medium">{entity?.name}</div>

        <InformationTile
          title="Default Authorization"
          subtitle={defaultAuthorization?.role}
        />
      </div>
    </HoverablePaper>
  );
};
